import { useEffect, useState } from 'react';

import { Cross } from '@assets/icons';
import LockedButton from '@components/locked-button/LockedButton';
import PermissionAccess from '@components/permission-access/PermissionAccess';
import { UserPermissions } from '@enums/user-permissions';

import { useCreateLabel } from './hooks/use-create-label';
import { LabelMetadataModal } from './LabelMetadataModal';
import { ContentModalActionType, LabelItem, LabelModalData } from './types';

type CreateNewLabelProp = {
  updateSelectedItems: (item: LabelItem) => void;
  isFilterMode?: boolean;
  isAssetLocked?: boolean;
};

export const CreateNewLabel = (prop: CreateNewLabelProp) => {
  const {
    updateSelectedItems,
    isFilterMode = false,
    isAssetLocked = false,
  } = prop;
  const [isOpen, setIsOpen] = useState(false);

  const {
    mutateAsync: createNewLabel,
    data: createdLabel,
    isLoading,
    isSuccess,
  } = useCreateLabel();

  useEffect(() => {
    if (isSuccess && !isFilterMode && !isAssetLocked) {
      updateSelectedItems(createdLabel);
    }
  }, [isSuccess, isFilterMode, isAssetLocked]);

  const handleCreateNewLabel = async (labelData: LabelModalData) => {
    try {
      await createNewLabel({ ...labelData });
    } catch (error) {
      console.error(error);
    } finally {
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  return (
    <>
      <PermissionAccess
        permission={UserPermissions.MANAGE_BRAND_LIBRARY}
        AllowedContent={
          <button
            className="flex gap-2.5 items-center px-2 py-1 rounded text-button-secondary-default-text font-bold text-sm"
            onClick={handleOpenModal}
          >
            <Cross className="rotate-45 h-4 w-4 font-bold" />{' '}
            <div> Create new</div>
          </button>
        }
        DeniedContent={
          <LockedButton
            buttonText="Create new"
            buttonClassname="flex gap-2.5 items-center px-2 py-1 rounded text-button-secondary-default-text font-bold text-sm w-full"
            LeftIconComponent={Cross}
            iconClassName="rotate-45 h-4 w-4 font-bold"
          />
        }
      />
      {isOpen && (
        <LabelMetadataModal
          isOpen={isOpen}
          data={{ name: '', metadata: {} }}
          isLoading={isLoading}
          type={ContentModalActionType.CREATE_LABEL}
          handleCancel={handleCancel}
          handleSave={handleCreateNewLabel}
        />
      )}
    </>
  );
};
